<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Link, link } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Input, Row, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { formatBytes } from '@common/utils/numbers';

  const { basepath } = getContext('config');
  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let sender = '';
  let recipient = '';
  let loading = false;
  let results = null;
  let targeting = {};
  let defaultValue = '';

  const onPreview = async () => {
    results = null;

    loading = true;

    results = await TargetingService.preview({ sender, recipient });

    loading = false;
  };

  const onSave = async () => {
    loading = true;

    targeting.default = defaultValue;

    const response = await TargetingService.update(targeting);

    loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.TARGETING_UPDATE'), 'error');
    } else {
      notify($format('label.SUCCESS.TARGETING_UPDATED'));
    }
  };

  onMount(async () => {
    loading = true;

    targeting = await TargetingService.get();

    if (targeting) {
      defaultValue = targeting.default || '';
    }

    loading = false;
  });

  $: disabled = !sender || !recipient;
</script>

<div id="targeting-diagnostics" class="page">
  <Loader show={loading} backdrop />
  <div class="page-header no-bottom-border">
    <Container fluid>
      <Row>
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/targeting" use:link>{$format('label.TARGETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.PREVIEW')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row>
        <Col>
          <h1 class="page-title">{$format('label.PREVIEW')}</h1>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="tabs">
      <div class="tab-list">
        <Link class="tab" to="/marketing/targeting/groups">{$format('label.GROUPS')}</Link>
        <Link class="tab" to="/marketing/targeting/domains">{$format('label.DOMAINS')}</Link>
        <Link class="tab" to="/marketing/targeting/recipients">{$format('label.RECIPIENTS')}</Link>
        <Link class="tab active" to="/marketing/targeting/diagnostics">{$format('label.PREVIEW')}</Link>
      </div>
    </div>
    <div class="diagnostic-content">
      <h2 class="pt-3">{$format('label.CONFIGURE')}</h2>

      <div class="pb-3">
        <b class="d-block py-2">
          {$format('label.DEFAULT_CAMPAIGN')}
          <Icon name="help-circle" id="tooltip-default-campaign" />
          <Tooltip target="tooltip-default-campaign">
            {$format('label.TOOLTIP.DEFAULT_CAMPAIGN')}
          </Tooltip>
        </b>
        <div class="d-flex gap-2 align-items-center">
          <Input type="text" bind:value={defaultValue} />
          <Button color="primary" on:click={onSave} disabled={loading}>
            {$format('label.SAVE')}
          </Button>
        </div>
      </div>

      <h2 class="pt-3">{$format('label.PREVIEW')}</h2>
      <div class="instructions">
        <Icon name="info" />
        <span>{$format('label.ABM_DIAGNOSTIC_INSTRUCTIONS')}</span>
      </div>
      <div class="form">
        <div class="form-item">
          <label for="sender">{$format('label.SENDER')}</label>
          <Input type="email" id="sender" placeholder="Enter email address" bind:value={sender} />
        </div>
        <div class="form-item">
          <label for="sender">{$format('label.RECIPIENT')}</label>
          <Input type="email" id="recipient" placeholder="Enter email address" bind:value={recipient} />
        </div>
        <div class="form-item">
          <Button color="primary" on:click={onPreview} {disabled}>{$format('label.PREVIEW')}</Button>
        </div>
      </div>

      {#if results}
        <h2>{$format('label.ABM_MATCH_RESULTS')}</h2>
        <div class="results">
          {#if results.match}
            {#if results.matchtype}
              <div class="results-section">
                <h4>{$format('label.TYPE')}</h4>
                <div>{results.matchtype}</div>
              </div>
            {/if}
            {#if results.matchmode === 'all'}
              <div class="results-section">
                <h4>{$format('label.MATCHED_TARGETS')} ({(results.matchindexes || []).length})</h4>
                <div>
                  {$format('label.ALL_MATCHES')}: {(results.matchindexes || []).map((i) => `#${i + 1}`).join(', ')}
                </div>
              </div>
            {/if}
            <div class="results-section">
              <h4>{$format('label.TAGS')} {results.tags.length ? `(${results.tags.length})` : ''}</h4>
              <div class="tags">
                {#if results.tags.length}
                  {#each results.tags as tag}
                    <div class="tag">{tag}</div>
                  {/each}
                {:else}
                  <div class="empty">{$format('label.NO_TAGS')}</div>
                {/if}
              </div>
            </div>
            <div class="results-section">
              <h4>{$format('label.CAMPAIGNS')} {results.ads.length ? `(${results.ads.length})` : ''}</h4>
              <div class="banners">
                {#if results.ads.length}
                  {#each results.ads as ad}
                    {#await CampaignsService.getById(ad)}
                      <div class="banner">
                        <div class="loading">
                          <Spinner show inline />
                        </div>
                      </div>
                    {:then campaign}
                      {#if campaign?.rulename}
                        <b>{campaign.rulename}</b>
                        {#if campaign.previewURL}
                          <div class="banner">
                            <img src={campaign.previewURL} alt={campaign.rulename} loading="lazy" />
                            {#if campaign?.metadata}
                              <div class="banner-details">
                                <small>{campaign.metadata.origfile}</small>
                                <small>
                                  {campaign.metadata.mimetype} -
                                  {campaign.metadata.imageinfo.width}x{campaign.metadata.imageinfo.height}
                                  ({formatBytes(campaign.metadata.filesize)})
                                </small>
                              </div>
                            {/if}
                          </div>
                        {/if}
                      {/if}
                    {:catch}
                      {$format('label.ERROR')}
                    {/await}
                  {/each}
                {:else}
                  <div class="empty">{$format('label.NO_CAMPAIGNS')}</div>
                {/if}
              </div>
            </div>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss" src="./diagnostics.scss"></style>
